/**
 * @license Copyright (c) 2003-2020, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module table/converters/table-cell-paragraph-post-fixer
 */

/**
 * Injects a table cell post-fixer into the model which inserts a `paragraph` element into empty table cells.
 *
 * A table cell must contain at least one block element as a child. An empty table cell will have an empty `paragraph` as a child.
 *
 *		<table>
 *			<tableRow>
 *				<tableCell></tableCell>
 *			</tableRow>
 *		</table>
 *
 * Will be fixed to:
 *
 *		<table>
 *			<tableRow>
 *				<tableCell><paragraph></paragraph></tableCell>
 *			</tableRow>
 *		</table>
 *
 * @param {module:engine/model/model~Model} model
 */
export default function injectTableCellParagraphPostFixer(model) {
  model.document.registerPostFixer(function (writer) {
    return tableCellContentsPostFixer(writer, model);
  });
} // The table cell contents post-fixer.
//
// @param {module:engine/model/writer~Writer} writer
// @param {module:engine/model/model~Model} model

function tableCellContentsPostFixer(writer, model) {
  var changes = model.document.differ.getChanges();
  var wasFixed = false;
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = changes[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var entry = _step.value;

      if (entry.type == 'insert' && entry.name == 'table') {
        wasFixed = fixTable(entry.position.nodeAfter, writer) || wasFixed;
      }

      if (entry.type == 'insert' && entry.name == 'tableRow') {
        wasFixed = fixTableRow(entry.position.nodeAfter, writer) || wasFixed;
      }

      if (entry.type == 'insert' && entry.name == 'tableCell') {
        wasFixed = fixTableCellContent(entry.position.nodeAfter, writer) || wasFixed;
      }

      if (checkTableCellChange(entry)) {
        wasFixed = fixTableCellContent(entry.position.parent, writer) || wasFixed;
      }
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return != null) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  return wasFixed;
} // Fixes all table cells in a table.
//
// @param {module:engine/model/element~Element} table
// @param {module:engine/model/writer~Writer} writer


function fixTable(table, writer) {
  var wasFixed = false;
  var _iteratorNormalCompletion2 = true;
  var _didIteratorError2 = false;
  var _iteratorError2 = undefined;

  try {
    for (var _iterator2 = table.getChildren()[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
      var row = _step2.value;
      wasFixed = fixTableRow(row, writer) || wasFixed;
    }
  } catch (err) {
    _didIteratorError2 = true;
    _iteratorError2 = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion2 && _iterator2.return != null) {
        _iterator2.return();
      }
    } finally {
      if (_didIteratorError2) {
        throw _iteratorError2;
      }
    }
  }

  return wasFixed;
} // Fixes all table cells in a table row.
//
// @param {module:engine/model/element~Element} tableRow
// @param {module:engine/model/writer~Writer} writer


function fixTableRow(tableRow, writer) {
  var wasFixed = false;
  var _iteratorNormalCompletion3 = true;
  var _didIteratorError3 = false;
  var _iteratorError3 = undefined;

  try {
    for (var _iterator3 = tableRow.getChildren()[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
      var tableCell = _step3.value;
      wasFixed = fixTableCellContent(tableCell, writer) || wasFixed;
    }
  } catch (err) {
    _didIteratorError3 = true;
    _iteratorError3 = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion3 && _iterator3.return != null) {
        _iterator3.return();
      }
    } finally {
      if (_didIteratorError3) {
        throw _iteratorError3;
      }
    }
  }

  return wasFixed;
} // Fixes all table cell content by:
// - Adding a paragraph to a table cell without any child.
// - Wrapping direct $text in a `<paragraph>`.
//
// @param {module:engine/model/element~Element} table
// @param {module:engine/model/writer~Writer} writer
// @returns {Boolean}


function fixTableCellContent(tableCell, writer) {
  // Insert paragraph to an empty table cell.
  if (tableCell.childCount == 0) {
    writer.insertElement('paragraph', tableCell);
    return true;
  } // Check table cell children for directly placed text nodes.
  // Temporary solution. See https://github.com/ckeditor/ckeditor5/issues/1464.


  var textNodes = Array.from(tableCell.getChildren()).filter(function (child) {
    return child.is('text');
  });
  var _iteratorNormalCompletion4 = true;
  var _didIteratorError4 = false;
  var _iteratorError4 = undefined;

  try {
    for (var _iterator4 = textNodes[Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
      var child = _step4.value;
      writer.wrap(writer.createRangeOn(child), 'paragraph');
    } // Return true when there were text nodes to fix.

  } catch (err) {
    _didIteratorError4 = true;
    _iteratorError4 = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion4 && _iterator4.return != null) {
        _iterator4.return();
      }
    } finally {
      if (_didIteratorError4) {
        throw _iteratorError4;
      }
    }
  }

  return !!textNodes.length;
} // Checks if a differ change should fix the table cell. This happens on:
// - Removing content from the table cell (i.e. `tableCell` can be left empty).
// - Adding a text node directly into a table cell.
//
// @param {Object} differ change entry
// @returns {Boolean}


function checkTableCellChange(entry) {
  if (!entry.position || !entry.position.parent.is('tableCell')) {
    return false;
  }

  return entry.type == 'insert' && entry.name == '$text' || entry.type == 'remove';
}